import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { Subscription, fromEvent } from 'rxjs';

export interface AlTableApi {
  deselectAll(): void;
  isLoading: boolean;
}

export interface AlTableColumn {
  key: string;
  title: string;
  width?: string;
  /** Wie die Spalte umbricht. Ohne Angabe bricht eine Zelle nur zwischen
   *  Wörtern um, nie mitten im Wort (`.cell-content` im SCSS) — vorher
   *  schrumpfte eine Spalte ohne `width` auf ein Zeichen, und „Hochgeladen
   *  am" stand Buchstabe für Buchstabe untereinander (Wissensbasis,
   *  24.09.2026).
   *
   *  `date` / `number`: gleich breite Ziffern, damit die Spalte fluchtet.
   *  Ein Datum bricht höchstens zwischen Tag und Uhrzeit um.
   *  `name`: die Hauptspalte mit langen Werten ohne Leerzeichen (Dateiname,
   *  URL). Sie darf überall umbrechen, schrumpft dafür nie unter eine
   *  lesbare Mindestbreite (CI `.al-cell-name`).
   *  `text`: weitere Spalten mit langen Werten ohne Leerzeichen (E-Mail,
   *  ID, Audit-Ziel, Fundstelle). Brechen überall um; Untergrenze ist das
   *  längste Wort der Überschrift.
   *
   *  Reicht der Platz selbst dann nicht, scrollt die Tabelle waagerecht. */
  kind?: 'date' | 'number' | 'name' | 'text';
  sortable?: boolean;
  sortDefault?: 'asc' | 'desc' | 'none' | undefined;
  map?: (i: any, row: any) => any;
  component?: Type<unknown>;
  componentInputs?:
    | Record<string, any>
    | ((context: AlTableColumnComponentContext) => Record<string, any>);
}

export interface AlTableColumnComponentContext {
  value: any;
  row: any;
  column: AlTableColumn;
}

export interface AlTableRowAction {
  key?: string;
  title: string;
  icon: string;
  /** Zusaetzlich in der Auswahlleiste anbieten. Der Eintrag bleibt dabei auch
   *  im Zeilenmenue — "Loeschen" will man beides koennen. */
  isMultiAction?: boolean;
  /** Nur in der Auswahlleiste, nicht im Zeilenmenue. Fuer Aktionen, die es
   *  einzeln gar nicht gibt (z. B. "mehrere Kurse auf einmal zuordnen"):
   *  im Zeilenmenue waeren sie eine dritte Variante derselben Sache und
   *  taeten beim Klick nichts, weil die Zeile keine Auswahl ist. */
  multiOnly?: boolean;
  disabled?: boolean | ((row: any) => boolean);
  tooltip?: string | ((row: any) => string);
  action?: (value: any, row: any) => void;
}

export interface AlTableGlobalAction {
  /** Stable id; also marks the button for the agent as `tableaction.<key>`. */
  key?: string;
  title: string;
  icon: string;
  action?: (value: any, row: any) => void;
}

export interface AlTableOptions {
  resetPageOnDataChange?: boolean;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {
  protected defaultOptions: AlTableOptions = {
    resetPageOnDataChange: false,
  };

  @Input() title = 'Table';
  @Input() data: any[] = [];
  @Input() rowIdKey = 'id';
  /** Entity type of the rows (`bot`, `file`, …) — marks every row as
   *  `data-agent-target="<type>:<id>:row"` so the in-house agent can point
   *  at it. Leave unset for tables whose rows are no agent-known objects. */
  @Input() agentEntityType: string | null = null;
  @Input() columns: AlTableColumn[] = [];

  @Input() rowActions: AlTableRowAction[] = [];

  @Input() globalActions: AlTableGlobalAction[] = [];

  @Input() maxVisibleItems = 10;

  /** Row selection. Off for read-only listings (an audit log has nothing to
   *  select), where an inert checkbox column only suggests actions that do
   *  not exist. Default keeps every existing table unchanged. */
  @Input() selectable = true;

  /** The built-in pager works on `data`, i.e. what the page already loaded.
   *  A page that pages on the server turns it off and renders its own, so the
   *  two do not contradict each other. */
  @Input() showPagination = true;

  @Input() options?: AlTableOptions;

  /** Zusatztext in der Auswahlleiste neben "n Ausgewählt", z. B. die
   *  Gesamtgröße markierter Dateien. Bekommt alle ausgewählten Zeilen (über
   *  alle Seiten), `null` blendet ihn aus. Gerechnet wird nur, wenn sich
   *  die Auswahl oder `data` ändern, nicht im Template; der Aufrufer
   *  übergibt eine feste Funktion, keinen Getter (Regel 18). */
  @Input() selectionSummary?: (rows: any[]) => string | null;

  @Output() event = new EventEmitter<{ event: string; data: any }>();

  selectedRow = new Set<number | string>();
  selectionSummaryText: string | null = null;
  currentPage = 0;
  isSelectAllChecked = false;
  isLoading = false;

  sortingColumn?: AlTableColumn;
  sortingDirection: 'asc' | 'desc' | 'none' = 'none';
  openedRowMenuId?: number | string;

  /** Das Zeilenmenü öffnet im CDK-Overlay statt absolut in der Zelle. In
   *  der Zelle würde es vom waagerechten Scrollbereich der Tabelle
   *  abgeschnitten (`overflow-x: auto` erzwingt auch `overflow-y`). Nebular
   *  lenkt den CDK-Overlay-Container auf `NbOverlayContainerAdapter` um —
   *  das Menü landet damit innerhalb `nb-layout`, erbt die CI-Tokens und
   *  die Overlay-Z-Index-Staffel aus styles.scss. */
  readonly rowMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  ];

  isNumeric(column: AlTableColumn): boolean {
    return column.kind === 'date' || column.kind === 'number';
  }

  isName(column: AlTableColumn): boolean {
    return column.kind === 'name';
  }

  isText(column: AlTableColumn): boolean {
    return column.kind === 'text';
  }

  /** Solange ein Zeilenmenü offen ist: jedes Scrollen schließt es. */
  private rowMenuScroll?: Subscription;

  constructor(private zone: NgZone) {}

  @HostListener('document:click')
  onDocumentClick() {
    this.closeRowMenu();
  }

  closeRowMenu() {
    this.openedRowMenuId = undefined;
    this.rowMenuScroll?.unsubscribe();
    this.rowMenuScroll = undefined;
  }

  ngOnDestroy() {
    this.rowMenuScroll?.unsubscribe();
  }

  /** Das Overlay folgt seiner Zeile nur beim Scrollen der Seite (CDK hört auf
   *  `document`-Scroll). Scrollt ein Behälter — der Dialogkörper im
   *  Treffer-Dialog, `.table-scroll` im schmalen Fenster —, bliebe das Menü
   *  stehen und zeigte neben einer anderen Zeile; „Löschen" träfe dann die
   *  falsche. Deshalb schließt jedes Scrollen außerhalb des Menüs es, wie
   *  ein natives Menü. `capture`, weil Scroll-Ereignisse nicht aufsteigen. */
  private closeRowMenuOnScroll() {
    if (this.rowMenuScroll) return;
    this.zone.runOutsideAngular(() => {
      this.rowMenuScroll = fromEvent(document, 'scroll', {
        capture: true,
        passive: true,
      }).subscribe((event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('.row-action-menu')) {
          return;
        }
        this.zone.run(() => this.closeRowMenu());
      });
    });
  }

  /** Menü ist im Overlay — also nicht mehr der nächste Tab-Stopp nach ⋮.
   *  Der Fokus springt deshalb auf den ersten Eintrag (Muster „Menübutton").
   *  Nach dem Einhängen erst im nächsten Takt: die Einträge rendert das
   *  `*ngFor` im Overlay erst im folgenden Change-Detection-Lauf. */
  onRowMenuAttached(overlay: CdkConnectedOverlay) {
    setTimeout(() => {
      overlay.overlayRef?.overlayElement
        .querySelector<HTMLElement>('.row-action-item:not(.is-disabled)')
        ?.focus({ preventScroll: true });
    });
  }

  ngOnInit() {
    this.options = { ...this.defaultOptions, ...this.options };

    this.columns.forEach((column) => {
      if (column.sortDefault) {
        this.onSort(column, column.sortDefault);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.data = changes['data'].currentValue || [];

      if (this.shouldResetPageOnDataChange()) {
        this.currentPage = 0;
      } else {
        this.currentPage = this.getClampedPageIndex(this.currentPage);
      }

      // Not `deselectAll()`. A page that binds a getter to `[data]` hands us a
      // new array instance on every change-detection run, and clearing the
      // whole selection there means the checkboxes empty themselves again a
      // tick after they were ticked — the bulk actions then never see a row.
      // Dropping only the rows that are actually gone keeps the intent
      // (nothing stays selected that is no longer on screen) without that.
      this.pruneSelection();
    } else if (changes['selectionSummary']) {
      this.refreshSelectionSummary();
    }
  }

  /** Keep only the ids still present in `data`. */
  private pruneSelection() {
    if (!this.selectedRow.size) {
      this.isSelectAllChecked = false;
      this.refreshSelectionSummary();
      return;
    }

    const present = new Set(this.data.map((row) => row?.[this.rowIdKey]));
    for (const id of [...this.selectedRow]) {
      if (!present.has(id)) {
        this.selectedRow.delete(id);
      }
    }

    this.isSelectAllChecked =
      this.data.length > 0 && this.selectedRow.size === this.data.length;
    this.refreshSelectionSummary();
  }

  private refreshSelectionSummary() {
    if (!this.selectionSummary || !this.selectedRow.size) {
      this.selectionSummaryText = null;
      return;
    }
    const rows = this.data.filter((r) => this.selectedRow.has(r?.[this.rowIdKey]));
    this.selectionSummaryText = this.selectionSummary(rows) || null;
  }

  onSort(column: AlTableColumn, direction?: 'asc' | 'desc' | 'none') {
    if (direction) {
      this.sortingDirection = direction;
      this.sortingColumn = column;
      return;
    }

    if (column.key !== this.sortingColumn?.key) {
      this.sortingDirection = 'none';
    }
    this.sortingColumn = column;

    if (this.sortingDirection === 'none') {
      this.sortingDirection = 'asc';
    } else if (this.sortingDirection === 'asc') {
      this.sortingDirection = 'desc';
    } else if (this.sortingDirection === 'desc') {
      this.sortingDirection = 'none';
    }
  }

  onCheckBoxChange(isChecked: boolean, id?: number | string) {
    if (typeof id === 'undefined') {
      // If the id is not provided, it means the checkbox is the select all checkbox
      if (isChecked) {
        this.selectedRow = new Set(this.data.map((row) => row[this.rowIdKey]));
        this.isSelectAllChecked = true;
      } else {
        this.isSelectAllChecked = false;
        this.selectedRow.clear();
      }
    } else {
      if (isChecked) {
        this.selectedRow.add(id);
      } else {
        this.selectedRow.delete(id);
      }
    }
    this.refreshSelectionSummary();
  }

  onPageChanged(page: number) {
    this.currentPage = page;
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.maxVisibleItems = itemsPerPage;
    this.currentPage = 0;
  }

  getVisibleData(data: any[]) {
    return data.slice(
      this.currentPage * this.maxVisibleItems,
      (this.currentPage + 1) * this.maxVisibleItems
    );
  }

  getFilteredData(data: any[]) {
    return data;
  }

  getSortedData(data: any[]) {
    if (this.sortingDirection === 'none' || !this.sortingColumn) {
      return data;
    }

    data = new Array(...data).sort((rowA, rowB) => {
      const aValue = rowA[this.sortingColumn!.key];
      const bValue = rowB[this.sortingColumn!.key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (this.sortingDirection === 'asc') {
          return bValue.localeCompare(aValue);
        }
        return aValue.localeCompare(bValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (this.sortingDirection === 'asc') {
          return bValue - aValue;
        }
        return aValue - bValue;
      }

      return 0;
    });

    return data;
  }

  get finalData() {
    let data = this.data;

    data = this.getSortedData(data);
    data = this.getFilteredData(data);

    return this.getVisibleData(data);
  }

  deselectAll() {
    this.selectedRow.clear();
    this.isSelectAllChecked = false;
    this.selectionSummaryText = null;
  }

  /** Stable identities for the three loops that render this table.
   *
   *  `columns`, `finalData` and `getRowActions(row)` are all recomputed on
   *  every change-detection run — `finalData` slices a new array, and callers
   *  routinely bind a getter to `[columns]` / `[rowActions]`. Without a
   *  trackBy, Angular compares by object identity, finds nothing it knows and
   *  throws the whole subtree away each tick. The visible result is a table
   *  whose buttons cannot be clicked: `mousedown` lands on one element,
   *  the node is replaced, and `mouseup` happens on its successor, so no
   *  `click` is ever produced. Hover styling and tooltips die the same way.
   */
  trackColumn = (_: number, column: AlTableColumn) => column?.key ?? _;

  trackRow = (_: number, row: any) => row?.[this.rowIdKey] ?? _;

  trackAction = (_: number, action: AlTableRowAction) =>
    action?.key ?? action?.title ?? _;

  mapValue(value: any, row: any, mapFunction?: Function) {
    if (mapFunction) {
      return mapFunction(value, row);
    }
    return value;
  }

  onGloablActionClick(action: any) {
    this.event.emit({
      event: 'globalAction',
      data: {
        action: action,
      },
    });
  }

  onRowActionClick(action: any, row: any) {
    if (!row) return;
    if (action.disabled) return;
    this.event.emit({
      event: 'rowAction',
      data: {
        action,
        id: row[this.rowIdKey],
        row,
      },
    });
  }

  toggleRowMenu(event: MouseEvent, row: any) {
    event.preventDefault();
    event.stopPropagation();
    const rowId = row?.[this.rowIdKey];
    if (String(this.openedRowMenuId) === String(rowId)) {
      this.closeRowMenu();
    } else {
      this.openedRowMenuId = rowId;
      this.closeRowMenuOnScroll();
    }
  }

  isRowMenuOpen(row: any) {
    return String(this.openedRowMenuId) === String(row?.[this.rowIdKey]);
  }

  /** Das Overlay hat sich gelöst. Zustand nur nachziehen, wenn es das Menü
   *  der offenen Zeile war (Escape schließt das Overlay selbst). Wechselt man
   *  von Zeile A zu Zeile B, löst sich A, während schon B offen ist — ein
   *  blindes `closeRowMenu()` schlösse dabei B gleich mit. */
  onRowMenuDetached(row: any, origin: CdkOverlayOrigin) {
    if (this.isRowMenuOpen(row)) {
      this.closeRowMenu();
      // Escape: der Fokus kehrt zum ⋮ der Zeile zurück, statt im Nichts zu
      // landen (das Menü, das ihn hatte, ist weg).
      (origin.elementRef.nativeElement as HTMLElement).focus({ preventScroll: true });
    }
  }

  onRowMenuActionClick(event: MouseEvent, action: AlTableRowAction, row: any) {
    event.preventDefault();
    event.stopPropagation();
    if (action.disabled) return;
    this.closeRowMenu();
    this.onRowActionClick(action, row);
  }

  onMultiActionClick(action: any) {
    this.event.emit({
      event: 'multiAction',
      data: {
        action,
        ids: Array.from(this.selectedRow),
        rows: this.data.filter((r) => this.selectedRow.has(r[this.rowIdKey])),
      },
    });
  }

  resolveComponentInputs(column: AlTableColumn, row: any) {
    const context: AlTableColumnComponentContext = {
      value: row[column.key],
      row,
      column,
    };

    if (!column.componentInputs) {
      return context;
    }

    if (typeof column.componentInputs === 'function') {
      const customInputs = column.componentInputs(context) || {};
      return {
        ...context,
        ...customInputs,
      };
    }

    return {
      ...context,
      ...(column.componentInputs || {}),
    };
  }

  getRowActions(row: any) {
    return this.rowActions.filter((a) => !a.multiOnly).map((action) => {
      const disabled =
        typeof action.disabled === 'function'
          ? action.disabled(row)
          : !!action.disabled;
      const tooltip =
        typeof action.tooltip === 'function'
          ? action.tooltip(row)
          : action.tooltip;

      return {
        ...action,
        disabled,
        data: {
          ...(action as any).data,
          row,
          rowId: row?.[this.rowIdKey],
          tooltip,
        },
      };
    });
  }

  private shouldResetPageOnDataChange() {
    return (
      this.options?.resetPageOnDataChange ??
      this.defaultOptions.resetPageOnDataChange ??
      false
    );
  }

  private getMaxPageIndex() {
    const totalItems = this.data.length;
    const itemsPerPage = this.maxVisibleItems || 1;

    if (totalItems === 0) {
      return 0;
    }

    return Math.max(Math.ceil(totalItems / itemsPerPage) - 1, 0);
  }

  private getClampedPageIndex(page: number) {
    if (page <= 0 || !Number.isFinite(page)) {
      return 0;
    }

    const maxPageIndex = this.getMaxPageIndex();

    if (page > maxPageIndex) {
      return maxPageIndex;
    }

    return page;
  }
}
